import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useSpotlight } from "../hooks/useSpotlight";
import TextReveal from "../components/TextReveal/TextReveal";
import GitHubStats from "../components/GitHubStats/GitHubStats";
import ProfileScene from "../components/Morph/scenes/ProfileScene";
import { usePhoneScene, useStoryMode } from "../hooks/useStoryMode";
import { BIO, GITHUB_USER, LOCATION, SKILLS } from "../data/profile";
import { DUR, EASE, STAGGER, VIEWPORT } from "../lib/motion";
import "./About.css";

/**
 * A tile in the bento grid.
 *
 * These used to tilt in 3D under the cursor. The tilt was dropped, not toned
 * down: it fought the page's own scroll transform, it blurred text on the
 * rotated axis, and twelve cards each pivoting independently made the grid feel
 * unstable — the opposite of what a spec sheet should feel like. What survives
 * is the cursor-tracked border glow, which needs the same `--mx`/`--my` the
 * tilt was already publishing but costs nothing but a gradient.
 */
function BentoCard({
    className = "",
    children,
    index = 0,
}: {
    className?: string;
    children: React.ReactNode;
    index?: number;
}) {
    const ref = useRef<HTMLDivElement>(null);
    useSpotlight(ref);
    return (
        <motion.div
            ref={ref}
            className={`bento-card ${className}`}
            /*
             * Tips up into place rather than sliding. The grid supplies the
             * perspective (About.css), so the whole row shares one vanishing
             * point and reads as a single surface settling.
             *
             * Worth distinguishing from the scroll-driven page flip this site
             * removed: that held sections at up to 65° for as long as they
             * were on screen, which meant text was permanently rendered on a
             * tilted plane and permanently soft. This is one-shot, lands at
             * exactly 0°, and is over in half a second — the type spends its
             * entire readable life flat.
             */
            /*
             * Opens from a vertical cut down its middle — the same "one block
             * splitting into tiles" idea as the desktop Profile scene, as a
             * one-shot reveal that suits a phone's free scrolling.
             */
            initial={{ opacity: 0.3, clipPath: "inset(0% 50% 0% 50% round 14px)" }}
            whileInView={{ opacity: 1, clipPath: "inset(0% 0% 0% 0% round 14px)" }}
            viewport={VIEWPORT}
            transition={{ duration: DUR.slow, delay: index * STAGGER * 1.5, ease: EASE }}
            style={{ "--i": index } as React.CSSProperties}
        >
            <div className="bento-card-inner">{children}</div>
            <span className="bento-beat" aria-hidden="true" />
        </motion.div>
    );
}

function LiveTime() {
    const [time, setTime] = useState(() => new Date());
    useEffect(() => {
        const id = setInterval(() => setTime(new Date()), 1000);
        return () => clearInterval(id);
    }, []);
    const hh = String(time.getHours()).padStart(2, "0");
    const mm = String(time.getMinutes()).padStart(2, "0");
    const ss = String(time.getSeconds()).padStart(2, "0");
    return (
        <>
            <span className="bento-label">Local time · Vietnam</span>
            <span className="bento-time mono">
                {hh}:{mm}<span className="bento-time-sec">:{ss}</span>
            </span>
            <span className="bento-sub">GMT+7 · {time.toLocaleDateString("en-US", { weekday: "long" })}</span>
        </>
    );
}

export default function About() {
    const story = useStoryMode();
    const phone = usePhoneScene();
    if (story || phone) return <ProfileScene phone={!story} />;

    return (
        <div className="about-page">
            <div className="about-container">
                <div className="about-hero">
                    <TextReveal as="h1" className="about-title" mutedFrom={1}>
                        About Me
                    </TextReveal>
                    <TextReveal as="p" className="about-subtitle" delay={0.14}>
                        Passionate developer crafting digital experiences with code.
                    </TextReveal>
                </div>

                {/* Bento Grid */}
                <div className="bento-grid">
                    {/* Bio — big card */}
                    <BentoCard className="bento-bio" index={0}>
                        <span className="bento-label">{BIO.label}</span>
                        <h2 className="bento-heading">{BIO.heading}</h2>
                        <p className="bento-paragraph">{BIO.body}</p>
                    </BentoCard>

                    {/* Status */}
                    <BentoCard className="bento-status" index={1}>
                        <span className="bento-label">Status</span>
                        <div className="bento-status-row">
                            <span className="status-dot-lg" />
                            <span className="bento-status-text">Available</span>
                        </div>
                        <span className="bento-sub">Open to opportunities</span>
                    </BentoCard>

                    {/* Location */}
                    <BentoCard className="bento-location" index={2}>
                        <span className="bento-label">Based in</span>
                        <h3 className="bento-big">{LOCATION.country}</h3>
                        <span className="bento-sub mono">
                            {LOCATION.lat.toFixed(2)}°N · {LOCATION.lon.toFixed(2)}°E
                        </span>
                    </BentoCard>
                    {/* Live time */}
                    <BentoCard className="bento-clock" index={3}>
                        <LiveTime />
                    </BentoCard>

                    {/* GitHub stats — custom heatmap */}
                    <BentoCard className="bento-github" index={4}>
                        <GitHubStats username={GITHUB_USER} />
                    </BentoCard>

                    {/* Skills */}
                    <BentoCard className="bento-skills" index={5}>
                        <span className="bento-label">Stack</span>
                        <div className="bento-skills-grid">
                            {SKILLS.map((skill) => (
                                <span key={skill} className="bento-skill mono">
                                    {skill}
                                </span>
                            ))}
                        </div>
                    </BentoCard>
                </div>
            </div>
        </div>
    );
}
